import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, getDoc, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth, ROLES, PERMISSIONS } from '../../hooks/useAuth';
import OrderDetails from '../orders/OrderDetails';
import CustomerCard from './CustomerCard';
import { ORDER_STATUS, ORDER_STATUS_LABELS, PAGINATION_OPTIONS } from '../../constants';
import { formatDate, formatDateForInput, formatPrice } from '../../utils';
import './CustomerList.css';

const CustomerList = ({ onCustomerChange, userRole, userId }) => {
  const { user, permissions, isMontajci, isYazilimci } = useAuth();

  // Telefon numarası formatlama (5XX XXX XX XX)
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  // TC Kimlik Numarası Doğrulama Algoritması
  const isTCValid = (tc) => {
    if (!tc) return true;
    const digits = tc.toString().replace(/\D/g, '').split('').map(Number);

    // Vergi numarası 10 haneli olur, TC 11 haneli. 10 hane ise TC doğrulaması yapma.
    if (digits.length === 10) return true;

    if (digits.length !== 11) return false;
    if (digits[0] === 0) return false;

    const d = digits;
    const oddSum = d[0] + d[2] + d[4] + d[6] + d[8];
    const evenSum = d[1] + d[3] + d[5] + d[7];

    // 10. hane kontrolü
    const d10 = (oddSum * 7 - evenSum) % 10;
    if ((d10 < 0 ? d10 + 10 : d10) !== d[9]) return false;

    // 11. hane kontrolü
    if ((oddSum + evenSum + d[9]) % 10 !== d[10]) return false;

    return true;
  };
  const [customers, setCustomers] = useState([]);
  // filteredCustomers useMemo ile hesaplanıyor — ayrı state gerekmez
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone1: '',
    phone2: '',
    email: '',
    address: '',
    notes: '',
    inspectionDate: '',
    orderDate: '',
    deliveryDate: '',
    orderStatus: ORDER_STATUS.NEW_ORDER,
    paidAmount: '',
    isPaymentCompleted: false,
    remainingAmount: 0,
    tcNo: ''
  });
  const [isAdding, setIsAdding] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isCustomerInfoCollapsed, setIsCustomerInfoCollapsed] = useState(false); // Müşteri bilgileri için
  const [lastSubmitTime, setLastSubmitTime] = useState(0); // Çifte submit koruması için
  const isSubmittingRef = useRef(false); // Immediate blocking için

  // Aşamalı işlem için step state'i
  const [currentStep, setCurrentStep] = useState('customer'); // 'customer' veya 'order'

  // Filtreleme ve arama state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState('table'); // 'table' veya 'card'
  const [remainingAmounts, setRemainingAmounts] = useState({}); // Müşteri ID'lerine göre kalan ödeme

  // Toast notification gösterme fonksiyonu
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Kalan ödeme hesaplama fonksiyonu
  const calculateRemainingAmount = useCallback(async (customer) => {
    try {
      // Ödeme tamamlandıysa kalan 0
      if (customer.isPaymentCompleted) {
        return 0;
      }

      // Ödenen tutar
      const paid = parseFloat(customer.paidAmount) || 0;

      // Siparişleri çek
      const ordersQuery = query(
        collection(db, 'orders'),
        where('customerId', '==', customer.id)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const orders = ordersSnapshot.docs.map(doc => doc.data());

      if (orders.length === 0) {
        return -1; // Sipariş yok anlamına gelir
      }

      // Toplam tutar
      const totalAmount = orders.reduce((sum, order) => {
        return sum + parseFloat(order.totalPrice || 0);
      }, 0);

      // İndirimli tutar
      const discountedTotalAmount = orders.reduce((sum, order) => {
        if (order.discountedPrice && parseFloat(order.discount) > 0) {
          return sum + parseFloat(order.discountedPrice || 0);
        } else {
          return sum + parseFloat(order.totalPrice || 0);
        }
      }, 0);

      // Genel Toplam öncelik zincirine göre hesaplanır (OrderDetails ile aynı mantık)
      const iskYuzde = parseFloat(customer.genelIskontoYuzde) || 0;
      const vadYuzde = parseFloat(customer.vadeliYuzde) || 0;

      const iskontoluToplam = totalAmount * (1 - iskYuzde / 100);
      const vadeliToplamVal = totalAmount * (1 + vadYuzde / 100);

      const grandTotal = vadYuzde > 0
        ? vadeliToplamVal
        : iskYuzde > 0
          ? iskontoluToplam
          : discountedTotalAmount !== totalAmount
            ? discountedTotalAmount
            : totalAmount;

      return grandTotal - paid;
    } catch (error) {
      console.error('Kalan ödeme hesaplanırken hata:', error);
      return 0;
    }
  }, []);

  // Müşterileri Firebase'den çek
  const fetchCustomers = useCallback(async () => {
    try {
      setIsLoading(true);

      let customersQuery;

      // Yazılımcı rolü: sadece kendi oluşturduğu müşterileri gör
      // orderBy server-side sıralama — client-side sort() gereksiz hale geliyor
      if (isYazilimci && userId) {
        customersQuery = query(
          collection(db, 'customers'),
          where('createdBy', '==', userId),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Admin, Satışcı: tüm müşterileri gör
        customersQuery = query(
          collection(db, 'customers'),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(customersQuery);
      let customersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Eğer data içinde 'id' field'ı varsa temizle, document ID'yi kullan
        const { id: dataId, ...cleanData } = data;

        if (dataId && dataId !== doc.id) {
          console.warn('⚠️ Veri tutarsızlığı tespit edildi:', {
            documentId: doc.id,
            dataId: dataId,
            customerName: data.name
          });
        }

        return {
          id: doc.id, // Her zaman Firebase document ID'si kullan
          ...cleanData
        };
      });

      // Montajcı rolü: sadece sipariş durumu "Montaj" olan müşterileri gör
      if (isMontajci) {
        customersData = customersData.filter(c => c.orderStatus === ORDER_STATUS.MONTAJ);
      }

      // Sıralama artık server-side (orderBy) — client-side sort() kaldırıldı
      setCustomers(customersData);

      // Kalan ödemeleri hesapla - önce müşteri kaydındaki remainingAmount'u kullan, yoksa hesapla
      const amounts = {};
      for (const customer of customersData) {
        // Müşteri kaydında zaten remainingAmount varsa onu kullan
        // Not: -1 değeri "sipariş yok" anlamına gelir, bu değeri de kabul et
        if (customer.remainingAmount !== undefined && customer.remainingAmount !== null && customer.remainingAmount !== 0) {
          amounts[customer.id] = parseFloat(customer.remainingAmount);
        } else if (customer.isPaymentCompleted) {
          // Ödeme tamamlandıysa 0
          amounts[customer.id] = 0;
        } else {
          // Yoksa veya 0 ise undefined olarak bırak, sonra lazy olarak hesaplanacak (performans için)
          // 0 değeri hem "ödeme alındı" hem "sipariş yok" olabilir, bu yüzden tekrar hesaplanmalı
          // undefined olursa "..." gösterilecek, hesaplanana kadar
          amounts[customer.id] = undefined;
        }
      }
      setRemainingAmounts(amounts);

      // Arka planda sadece remainingAmount olmayan veya 0 olan müşteriler için hesapla (lazy loading)
      // Bu işlemi async olarak yap, UI'ı bloklamasın
      // Hesaplanan değerleri Firebase'e kaydet ki bir sonraki yüklemede hızlı olsun
      // Not: remainingAmount 0 olanlar için de hesaplama yap çünkü "sipariş yok" (-1) olabilir
      // Hesaplanması gereken müşterileri ayır
      const needsCalc = customersData.filter(c =>
        (c.remainingAmount === undefined || c.remainingAmount === null || c.remainingAmount === 0)
        && !c.isPaymentCompleted
      );
      const completed = customersData.filter(c =>
        (c.remainingAmount === undefined || c.remainingAmount === null || c.remainingAmount === 0)
        && c.isPaymentCompleted
      );

      // Ödeme tamamlanmışlar için anında 0 yaz
      if (completed.length > 0) {
        const completedAmounts = Object.fromEntries(completed.map(c => [c.id, 0]));
        setRemainingAmounts(prev => ({ ...prev, ...completedAmounts }));
      }

      // Hesaplanması gerekenler için arka planda paralel hesapla (seri await döngüsü yok)
      if (needsCalc.length > 0) {
        setTimeout(async () => {
          try {
            // Tüm hesaplamaları aynı anda başlat — N sorgu seri değil paralel
            const results = await Promise.all(
              needsCalc.map(c => calculateRemainingAmount(c))
            );

            const missingAmounts = Object.fromEntries(
              needsCalc.map((c, i) => [c.id, results[i]])
            );

            setRemainingAmounts(prev => ({ ...prev, ...missingAmounts }));

            // Firebase'e paralel kaydet (arka planda)
            Promise.all(
              needsCalc.map((c, i) =>
                updateDoc(doc(db, 'customers', c.id), {
                  remainingAmount: results[i],
                  updatedAt: new Date()
                }).catch(err => {
                  console.warn(`Müşteri ${c.id} remainingAmount kaydedilemedi:`, err);
                })
              )
            );
          } catch (err) {
            console.warn('Paralel hesaplama hatası:', err);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
      showNotification('Müşteriler yüklenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [calculateRemainingAmount, isYazilimci, isMontajci, userId]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Müşterileri filtrele — useMemo ile tek render'da hesaplanır, gereksiz state yok
  const filteredCustomers = useMemo(() => {
    let filtered = customers;

    if (searchTerm) {
      const searchLower = searchTerm.toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(customer =>
        customer.name?.toLocaleLowerCase('tr-TR').includes(searchLower) ||
        customer.phone1?.includes(searchTerm) ||
        customer.address?.toLocaleLowerCase('tr-TR').includes(searchLower)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(customer => customer.orderStatus === statusFilter);
    }

    return filtered;
  }, [customers, searchTerm, statusFilter]);

  // Filtre değişince ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  // Sayfa değiştirme
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa başına öğe sayısını değiştirme
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Yeni müşteri ekle
  const handleAddCustomer = async (e) => {
    e.preventDefault();

    // IMMEDIATE BLOCKING - Hiçbir koşulda geçilmez
    if (isSubmittingRef.current) {
      console.log('🚫 IMMEDIATE BLOCK - Already submitting');
      return;
    }

    // Çifte submit koruması - 2 saniye içinde ikinci submit'i engelle
    const now = Date.now();
    if (now - lastSubmitTime < 2000) {
      console.log('⚠️ Çifte submit engellendi:', {
        timeDiff: now - lastSubmitTime,
        lastSubmit: new Date(lastSubmitTime).toLocaleTimeString()
      });
      showNotification('Lütfen bekleyin, işlem devam ediyor...', 'warning');
      return;
    }

    // Zaten işlem devam ediyorsa engelleyelin
    if (isLoading) {
      console.log('⚠️ Loading state aktif, submit engellendi');
      return;
    }

    if (!newCustomer.name || !newCustomer.phone1) {
      showNotification('Ad Soyad ve Telefon 1 alanları zorunludur!', 'warning');
      return;
    }

    // TC No Doğrulaması
    if (newCustomer.tcNo && !isTCValid(newCustomer.tcNo)) {
      showNotification('Lütfen geçerli bir TC Kimlik Numarası giriniz!', 'error');
      return;
    }

    try {
      // İşlem başladığını hemen işaretle
      isSubmittingRef.current = true;
      setLastSubmitTime(now);
      setIsLoading(true);

      console.log('📝 Müşteri ekleme başladı:', new Date().toLocaleTimeString());

      // ID field'ını temizle (Firebase kendi document ID'sini verecek)
      const { id, ...customerDataWithoutId } = newCustomer;

      const customerData = {
        ...customerDataWithoutId,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null // Hangi kullanıcı oluşturduğu
      };

      console.log('🧹 ID temizlendi, kayıt yapılıyor:', {
        removedId: id,
        hasNameField: !!customerData.name
      });

      // Müşteriyi Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'customers'), customerData);

      console.log('✅ Müşteri Firebase\'e kaydedildi:', docRef.id);

      // Kaydedilen müşterinin ID'sini al
      const savedCustomer = { ...customerData, id: docRef.id };

      // Sipariş aşamasına geç
      setCurrentStep('order');
      setNewCustomer(savedCustomer);

      // Müşteri listesini in-memory güncelle (Firebase read maliyetini önlemek için)
      setCustomers(prev => [savedCustomer, ...prev]);

      if (onCustomerChange) {
        onCustomerChange();
      }

      // Ekranı yukarı scroll et
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

      showNotification('Müşteri başarıyla kaydedildi! Şimdi sipariş detaylarını ekleyebilirsiniz.', 'success');
    } catch (error) {
      console.error('❌ Müşteri eklenirken hata:', error);
      showNotification('Müşteri eklenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      // İşlem bitti, blokları kaldır
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Müşteri ekleme işlemini tamamla
  const handleCompleteCustomer = async () => {
    try {
      // Müşteri bilgilerini güncelle (sipariş durumu dahil)
      if (newCustomer.id && !newCustomer.id.startsWith('temp_')) {
        console.log('🔄 Müşteri güncelleniyor:', {
          customerId: newCustomer.id,
          orderStatus: newCustomer.orderStatus
        });

        // Direkt Firebase'den müşteriyi kontrol et (state timing sorunu için)
        let customerDocData = null;
        try {
          const customerDoc = await getDoc(doc(db, 'customers', newCustomer.id));

          if (!customerDoc.exists()) {
            console.error('❌ Müşteri Firebase\'de bulunamadı:', newCustomer.id);
            showNotification('Müşteri bulunamadı. Sayfa yenilenecek.', 'error');
            await fetchCustomers();
            setIsAdding(false);
            setCurrentStep('customer');
            return;
          }

          console.log('✅ Müşteri Firebase\'de mevcut:', newCustomer.id);
          customerDocData = customerDoc.data();
        } catch (checkError) {
          console.error('❌ Müşteri kontrol hatası:', checkError);
          showNotification('Müşteri kontrolü yapılamadı: ' + checkError.message, 'error');
          return;
        }

        // Ödeme bilgilerini de kaydet
        const paidAmountValue = (newCustomer.paidAmount && newCustomer.paidAmount.toString().trim() !== '')
          ? parseFloat(newCustomer.paidAmount)
          : parseFloat(customerDocData?.paidAmount || 0);

        // Kalan tutarı mevcut calculateRemainingAmount fonksiyonu ile hesapla (kod tekrarını önlemek için)
        const remainingAmountValue = await calculateRemainingAmount({
          ...newCustomer,
          paidAmount: paidAmountValue,
          isPaymentCompleted: newCustomer.isPaymentCompleted || customerDocData?.isPaymentCompleted || false
        });

        // Ödeme tamamlandı durumunu güncelle (kalan 0.01'den az ise ve sipariş varsa true yap)
        const isPaymentCompletedValue = remainingAmountValue !== -1 && (newCustomer.isPaymentCompleted || customerDocData?.isPaymentCompleted || remainingAmountValue <= 0.01);

        await updateDoc(doc(db, 'customers', newCustomer.id), {
          orderStatus: newCustomer.orderStatus,
          vadeliYuzde: newCustomer.vadeliYuzde || '',
          genelIskontoYuzde: newCustomer.genelIskontoYuzde || '',
          paidAmount: paidAmountValue,
          isPaymentCompleted: isPaymentCompletedValue,
          remainingAmount: remainingAmountValue,
          updatedAt: new Date()
        });


        // remainingAmounts state'ini anında güncelle — fetchCustomers'ı beklemeden doğru değeri göster
        setRemainingAmounts(prev => ({ ...prev, [newCustomer.id]: remainingAmountValue }));

        // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
        setCustomers(prev =>
          prev.map(c =>
            c.id === newCustomer.id
              ? {
                  ...c,
                  orderStatus: newCustomer.orderStatus,
                  vadeliYuzde: newCustomer.vadeliYuzde || '',
                  genelIskontoYuzde: newCustomer.genelIskontoYuzde || '',
                  paidAmount: paidAmountValue,
                  isPaymentCompleted: isPaymentCompletedValue,
                  remainingAmount: remainingAmountValue,
                  updatedAt: new Date()
                }
              : c
          )
        );
      } else {
        console.warn('⚠️ Geçersiz customer ID, güncelleme atlanıyor:', newCustomer.id);
      }

      // Formu temizle
      setNewCustomer({
        name: '',
        phone1: '',
        phone2: '',
        email: '',
        address: '',
        notes: '',
        inspectionDate: '',
        orderDate: '',
        deliveryDate: '',
        orderStatus: ORDER_STATUS.NEW_ORDER,
        vadeliYuzde: '',
        genelIskontoYuzde: '',
        paidAmount: '',
        isPaymentCompleted: false,
        remainingAmount: 0,
        tcNo: ''
      });
      setIsAdding(false);
      setCurrentStep('customer');

      if (onCustomerChange) {
        onCustomerChange();
      }

      showNotification('Müşteri ve sipariş bilgileri başarıyla kaydedildi!', 'success');
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      showNotification('Müşteri güncellenirken bir hata oluştu: ' + error.message, 'error');
    }
  };

  // Sipariş durumu değişikliği için callback
  const handleNewCustomerOrderStatusChange = useCallback((status) => {
    setNewCustomer(prev => ({ ...prev, orderStatus: status }));
  }, []);

  // Vadeli yüzde değişikliği için callback (yeni müşteri)
  const handleNewCustomerVadeliYuzdeChange = useCallback((vadeliYuzde) => {
    setNewCustomer(prev => ({ ...prev, vadeliYuzde: vadeliYuzde }));
  }, []);

  // Genel iskonto yüzde değişikliği için callback (yeni müşteri)
  const handleNewCustomerGenelIskontoYuzdeChange = useCallback((genelIskontoYuzde) => {
    setNewCustomer(prev => ({ ...prev, genelIskontoYuzde: genelIskontoYuzde }));
  }, []);

  // Düzenleme modunda fiyat (vade/iskonto) kaydetme başarılı olduğunda
  const handleEditCustomerVadeliSaveSuccess = useCallback(async (pricingData) => {
    const { vadeliYuzde, genelIskontoYuzde } = pricingData;

    // editingCustomer state'ini güncelle (anlık görüntü için)
    if (editingCustomer) {
      const customerId = editingCustomer.id;
      const updatedCustomer = { ...editingCustomer, vadeliYuzde, genelIskontoYuzde };
      setEditingCustomer(updatedCustomer);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? updatedCustomer
            : customer
        )
      );

      // Kalan ödeme bilgisini yenile
      const newRemaining = await calculateRemainingAmount(updatedCustomer);
      setRemainingAmounts(prev => ({
        ...prev,
        [customerId]: newRemaining
      }));
    }

    if (onCustomerChange) {
      onCustomerChange();
    }

    showNotification('Fiyat bilgileri başarıyla güncellendi!', 'success');
  }, [editingCustomer, calculateRemainingAmount, onCustomerChange]);

  // Ödeme bilgisi güncellendiğinde sadece ilgili müşteriyi güncelle (tüm listeyi yenilemeden)
  const handleCustomerPaymentUpdate = useCallback((updatedCustomerInfo) => {
    // editingCustomer state'ini güncelle
    if (editingCustomer && editingCustomer.id === updatedCustomerInfo.id) {
      setEditingCustomer(updatedCustomerInfo);
    }

    // Müşteri listesindeki ilgili müşteriyi güncelle (tüm listeyi yenilemeden)
    setCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === updatedCustomerInfo.id
          ? { ...customer, ...updatedCustomerInfo }
          : customer
      )
    );

    // Kalan ödeme state'ini güncelle
    setRemainingAmounts(prev => ({
      ...prev,
      [updatedCustomerInfo.id]: updatedCustomerInfo.remainingAmount || 0
    }));

    if (onCustomerChange) {
      onCustomerChange();
    }

    showNotification('Ödeme bilgisi başarıyla güncellendi!', 'success');
  }, [editingCustomer, onCustomerChange]);

  // Sipariş durumu değiştiğinde müşteri listesini güncelle
  const handleOrderStatusChange = async (customerId, newStatus) => {
    // Customer ID geçerliliğini kontrol et
    if (!customerId || customerId.startsWith('temp_')) {
      showNotification('Geçersiz müşteri ID\'si. Sayfayı yenileyip tekrar deneyin.', 'error');
      return;
    }

    try {
      // Önce müşterinin var olup olmadığını kontrol et
      const customerExists = customers.find(c => c.id === customerId);

      if (!customerExists) {
        showNotification('Müşteri bulunamadı. Sayfa yenilenecek.', 'error');
        await fetchCustomers(); // Listeyi yenile
        return;
      }

      await updateDoc(doc(db, 'customers', customerId), {
        orderStatus: newStatus,
        updatedAt: new Date()
      });

      // editingCustomer state'ini güncelle
      if (editingCustomer && editingCustomer.id === customerId) {
        setEditingCustomer(prev => ({
          ...prev,
          orderStatus: newStatus
        }));
      }

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? { ...customer, orderStatus: newStatus }
            : customer
        )
      );

      if (onCustomerChange) {
        onCustomerChange();
      }

      showNotification('Sipariş durumu başarıyla güncellendi!', 'success');
    } catch (error) {
      console.error('Sipariş durumu güncellenirken hata:', error);

      // Eğer döküman bulunamadı hatası ise, UI'ı temizle
      if (error.code === 'not-found') {
        // Edit modunu kapat
        if (editingCustomer && editingCustomer.id === customerId) {
          setEditingCustomer(null);
          document.body.classList.remove('modal-open');
        }

        // Listeyi yenile
        await fetchCustomers();

        showNotification('Bu müşteri artık mevcut değil. Liste güncellendi.', 'warning');
      } else {
        showNotification('Sipariş durumu güncellenirken bir hata oluştu: ' + error.message, 'error');

        // Hata durumunda listeyi yenile
        await fetchCustomers();
      }
    }
  };

  // Müşteri kopyala (temel bilgileri alır, tarihler ve siparişler hariç)
  const handleCopyCustomer = async (customer) => {
    try {
      setIsLoading(true);

      // Sadece temel müşteri bilgilerini al (not, tarihler ve siparişler hariç)
      const copiedCustomerData = {
        name: customer.name + ' (Kopya)',
        phone1: customer.phone1 || '',
        phone2: customer.phone2 || '',
        email: customer.email || '',
        address: customer.address || '',
        tcNo: customer.tcNo || '',
        notes: '', // Not kopyalanmaz
        inspectionDate: '', // Keşif tarihi kopyalanmaz
        orderDate: '', // Sipariş tarihi kopyalanmaz
        deliveryDate: '', // Teslimat tarihi kopyalanmaz
        orderStatus: ORDER_STATUS.NEW_ORDER,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: -1, // Sipariş yok
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null // Hangi kullanıcı oluşturduğu
      };

      // Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'customers'), copiedCustomerData);

      // Local state'e ekle (tüm listeyi yenilemek yerine)
      const newCustomer = {
        id: docRef.id,
        ...copiedCustomerData
      };

      setCustomers(prevCustomers => [newCustomer, ...prevCustomers]);
      setRemainingAmounts(prev => ({ ...prev, [docRef.id]: -1 }));

      if (onCustomerChange) {
        onCustomerChange();
      }

      showNotification(`"${customer.name}" müşterisi kopyalandı!`, 'success');
    } catch (error) {
      console.error('Müşteri kopyalanırken hata:', error);
      showNotification('Müşteri kopyalanırken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri silme onayı
  const handleDeleteConfirm = (customer) => {
    setDeleteConfirm(customer);
  };

  // Müşteri sil
  const handleDeleteCustomer = async () => {
    if (!deleteConfirm) return;

    try {
      setIsLoading(true);

      const deletedId = deleteConfirm.id;
      await deleteDoc(doc(db, 'customers', deletedId));

      setDeleteConfirm(null);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers => prevCustomers.filter(customer => customer.id !== deletedId));
      setRemainingAmounts(prev => {
        const newAmounts = { ...prev };
        delete newAmounts[deletedId];
        return newAmounts;
      });

      if (onCustomerChange) {
        onCustomerChange();
      }

      showNotification('Müşteri başarıyla silindi!', 'success');
    } catch (error) {
      console.error('Müşteri silinirken hata:', error);
      showNotification('Müşteri silinirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri düzenleme modunu aç
  const handleEditCustomer = (customer) => {
    // Tarihleri input için uygun formata çevir
    const customerForEdit = {
      ...customer,
      inspectionDate: formatDateForInput(customer.inspectionDate),
      orderDate: formatDateForInput(customer.orderDate),
      deliveryDate: formatDateForInput(customer.deliveryDate)
    };

    setEditingCustomer(customerForEdit);

    // Modal açıldığında body scroll'u engelle
    document.body.classList.add('modal-open');

    // Edit modu açıldığında otomatik scroll
    setTimeout(() => {
      const editSection = document.querySelector('.edit-customer-section');
      if (editSection) {
        editSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Müşteri güncelle
  const handleUpdateCustomer = async (e) => {
    if (e) e.preventDefault();

    if (!editingCustomer.name || !editingCustomer.phone1) {
      showNotification('Ad Soyad ve Telefon 1 alanları zorunludur!', 'warning');
      return;
    }

    // TC No Doğrulaması
    if (editingCustomer.tcNo && !isTCValid(editingCustomer.tcNo)) {
      showNotification('Lütfen geçerli bir TC Kimlik Numarası giriniz!', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const customerId = editingCustomer.id;

      const updatedData = {
        name: editingCustomer.name,
        phone1: editingCustomer.phone1,
        phone2: editingCustomer.phone2,
        email: editingCustomer.email,
        address: editingCustomer.address,
        notes: editingCustomer.notes,
        inspectionDate: editingCustomer.inspectionDate,
        orderDate: editingCustomer.orderDate,
        deliveryDate: editingCustomer.deliveryDate,
        tcNo: editingCustomer.tcNo || '',
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'customers', customerId), updatedData);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? { ...customer, ...updatedData }
            : customer
        )
      );

      // Kalan ödemeyi hesapla ve güncelle
      const remaining = await calculateRemainingAmount(editingCustomer);
      setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));

      // Firebase'e remainingAmount kaydet
      await updateDoc(doc(db, 'customers', customerId), {
        remainingAmount: remaining
      });

      setEditingCustomer(null);
      document.body.classList.remove('modal-open');

      if (onCustomerChange) {
        onCustomerChange();
      }

      showNotification('Müşteri başarıyla güncellendi!', 'success');
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      showNotification('Müşteri güncellenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="customer-list-container">
      {/* Toast Notification */}
      {notification && (
        <div className={`toast-notification ${notification.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {notification.type === 'success' && <i className="fas fa-check-circle"></i>}
              {notification.type === 'error' && <i className="fas fa-exclamation-circle"></i>}
              {notification.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
            </div>
            <div className="toast-message">{notification.message}</div>
            <button
              className="toast-close"
              onClick={() => setNotification(null)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}

      <div className="customer-header">
        <h2>{isMontajci ? 'Montaj Listesi' : 'Müşteri Listesi'}</h2>
        {permissions?.canCreateCustomer && (
          <button
            onClick={() => setIsAdding(!isAdding)}
            className="add-customer-btn"
            disabled={isLoading}
          >
            {isAdding ? 'İptal' : '+ Yeni Müşteri'}
          </button>
        )}
      </div>

      {/* Filtreleme ve Arama */}
      {!isAdding && !editingCustomer && (
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Arama:</label>
              <input
                type="text"
                placeholder="Müşteri adı, telefon veya adres..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <label>Sipariş Durumu:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-filter"
              >
                <option value="">Tümü</option>
                {Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tablo Üstü Kontroller */}
      {!isAdding && !editingCustomer && (
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="filter-group">
              <label>Sayfa Başına:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                className="items-per-page"
              >
                {PAGINATION_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Tablo Görünümü"
              >
                <i className="fas fa-table"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => setViewMode('card')}
                title="Kart Görünümü"
              >
                <i className="fas fa-th-large"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdding && (
        <div className="add-customer-form">
          <h3>Yeni Müşteri Ekle</h3>

          {/* Aşama Göstergesi */}
          <div className="step-indicator">
            <div
              className={`step ${currentStep === 'customer' ? 'active' : 'completed'}`}
              onClick={() => setCurrentStep('customer')}
              style={{ cursor: 'pointer' }}
            >
              <span className="step-number">1</span>
              <span className="step-text">Müşteri Bilgileri</span>
            </div>
            <div
              className={`step ${currentStep === 'order' ? 'active' : ''}`}
              onClick={() => {
                if (newCustomer.id) { // Müşteri kaydedilmişse tıklanabilir
                  setCurrentStep('order');
                }
              }}
              style={{ cursor: newCustomer.id ? 'pointer' : 'not-allowed' }}
            >
              <span className="step-number">2</span>
              <span className="step-text">Sipariş Detayları</span>
            </div>
          </div>

          {/* Üst Kısım - Müşteri Bilgileri */}
          <div className={`customer-info-section ${currentStep === 'customer' ? 'active' : 'hidden'}`}>
            <h4>Müşteri Bilgileri</h4>
            <form onSubmit={handleAddCustomer}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>TC No / Vergi No</label>
                  <input
                    type="text"
                    value={newCustomer.tcNo}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setNewCustomer({ ...newCustomer, tcNo: value });
                    }}
                    placeholder="TC veya Vergi No"
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Telefon 1 *</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(newCustomer.phone1.replace(/^90/, ''))}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        const digits = formatted.replace(/\D/g, '');
                        setNewCustomer({ ...newCustomer, phone1: digits ? '90' + digits : '' });
                      }}
                      placeholder="5XX XXX XX XX"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Telefon 2</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(newCustomer.phone2.replace(/^90/, ''))}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        const digits = formatted.replace(/\D/g, '');
                        setNewCustomer({ ...newCustomer, phone2: digits ? '90' + digits : '' });
                      }}
                      placeholder="5XX XXX XX XX"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Adres</label>
                <textarea
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  rows="3"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group full-width">
                <label>Not</label>
                <textarea
                  value={newCustomer.notes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                  rows="3"
                  disabled={isLoading}
                />
              </div>

              <div className="form-grid date-grid">
                <div className="form-group">
                  <label>Keşif Tarihi</label>
                  <input
                    type="date"
                    value={newCustomer.inspectionDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, inspectionDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Sipariş Tarihi</label>
                  <input
                    type="date"
                    value={newCustomer.orderDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, orderDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Teslimat Tarihi</label>
                  <input
                    type="date"
                    value={newCustomer.deliveryDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, deliveryDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`save-btn ${(isLoading || isSubmittingRef.current) ? 'loading' : ''}`}
                  disabled={isLoading || isSubmittingRef.current}
                  style={{
                    pointerEvents: (isLoading || isSubmittingRef.current) ? 'none' : 'auto',
                    userSelect: 'none',
                    opacity: (isLoading || isSubmittingRef.current) ? 0.6 : 1
                  }}
                >
                  {(isLoading || isSubmittingRef.current) ? 'Kaydediliyor...' : 'Müşteriyi Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAdding(false);
                    setCurrentStep('customer');
                  }}
                  className="cancel-btn"
                  disabled={isLoading}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>

          {/* Alt Kısım - Sipariş Detayları */}
          <div className={`order-details-section ${currentStep === 'order' ? 'active' : 'hidden'}`}>
            <OrderDetails
              customerId={newCustomer.id || 'temp_' + Date.now()}
              customerName={newCustomer.name}
              customerInfo={newCustomer}
              isLoading={isLoading}
              orderStatus={newCustomer.orderStatus}
              onOrderStatusChange={handleNewCustomerOrderStatusChange}
              onVadeliYuzdeChange={handleNewCustomerVadeliYuzdeChange}
              onGenelIskontoYuzdeChange={handleNewCustomerGenelIskontoYuzdeChange}
              isEditMode={false}
              permissions={permissions}
              onCustomerSaved={(paymentInfo) => {
                // Ödeme bilgileri değiştiğinde newCustomer state'ini güncelle
                if (paymentInfo && typeof paymentInfo === 'object' && 'paidAmount' in paymentInfo) {
                  setNewCustomer(prev => ({
                    ...prev,
                    paidAmount: paymentInfo.paidAmount,
                    isPaymentCompleted: paymentInfo.isPaymentCompleted,
                    remainingAmount: paymentInfo.remainingAmount
                  }));
                }
              }}
            />

            {/* İşlemi Tamamla butonu sipariş detaylarının altında */}
            <div className="complete-form-actions">
              <button
                type="button"
                onClick={handleCompleteCustomer}
                className="complete-btn"
                disabled={isLoading}
              >
                İşlemi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCustomer && (
        <div className="edit-customer-section">
          <div className="edit-customer-header">
            <div className="header-left">
              <h3>Müşteri Düzenle</h3>
              <span className="auto-save-info">
                <i className="fas fa-info-circle"></i>
                Siparişler otomatik kaydedilir
              </span>
            </div>
            <button
              onClick={() => {
                // Düzenleme modundan çık
                const customerId = editingCustomer.id;
                setEditingCustomer(null);
                document.body.classList.remove('modal-open');

                // Sadece remainingAmount güncelle (local state'ten, ekstra Firebase read yok)
                (async () => {
                  try {
                    const customer = customers.find(c => c.id === customerId);
                    if (customer) {
                      const remaining = await calculateRemainingAmount(customer);
                      setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));
                      await updateDoc(doc(db, 'customers', customerId), {
                        remainingAmount: remaining,
                        updatedAt: new Date()
                      });
                    }
                  } catch (error) {
                    console.warn('Kalan ödeme güncellenirken hata:', error);
                  }
                })();
              }}
              className="close-edit-btn"
              title="Kapat"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="edit-customer-content">
            {/* Müşteri Bilgileri Bölümü */}
            <div className="customer-info-section">
              <div
                className="section-header collapsible"
                onClick={() => setIsCustomerInfoCollapsed(!isCustomerInfoCollapsed)}
              >
                <h4>Müşteri Bilgileri</h4>
                <i className={`fas fa-chevron-${isCustomerInfoCollapsed ? 'down' : 'up'}`}></i>
              </div>

              <div className={`section-content ${isCustomerInfoCollapsed ? 'collapsed' : ''}`}>
                <form onSubmit={handleUpdateCustomer}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Ad Soyad *</label>
                      <input
                        type="text"
                        value={editingCustomer.name}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>TC No / Vergi No</label>
                      <input
                        type="text"
                        value={editingCustomer.tcNo || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                          setEditingCustomer({ ...editingCustomer, tcNo: value });
                        }}
                        placeholder="TC veya Vergi No"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>Telefon 1 *</label>
                      <div className="phone-input-container">
                        <span className="phone-prefix">+90</span>
                        <input
                          type="tel"
                          className="phone-main-input"
                          value={formatPhoneNumber((editingCustomer.phone1 || '').replace(/^90/, ''))}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            const digits = formatted.replace(/\D/g, '');
                            setEditingCustomer({ ...editingCustomer, phone1: digits ? '90' + digits : '' });
                          }}
                          placeholder="5XX XXX XX XX"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Telefon 2</label>
                      <div className="phone-input-container">
                        <span className="phone-prefix">+90</span>
                        <input
                          type="tel"
                          className="phone-main-input"
                          value={formatPhoneNumber((editingCustomer.phone2 || '').replace(/^90/, ''))}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            const digits = formatted.replace(/\D/g, '');
                            setEditingCustomer({ ...editingCustomer, phone2: digits ? '90' + digits : '' });
                          }}
                          placeholder="5XX XXX XX XX"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={editingCustomer.email}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group full-width" style={{ marginTop: '10px' }}>
                    <label>Adres</label>
                    <textarea
                      value={editingCustomer.address}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Not</label>
                    <textarea
                      value={editingCustomer.notes}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, notes: e.target.value })}
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-grid" style={{ marginTop: '10px' }}>
                    <div className="form-group">
                      <label>Keşif Tarihi</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.inspectionDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, inspectionDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Sipariş Tarihi</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.orderDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, orderDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Teslimat Tarihi</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.deliveryDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, deliveryDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="save-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Güncelleniyor...' : 'Güncelle'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const customerId = editingCustomer.id;
                        setEditingCustomer(null);
                        document.body.classList.remove('modal-open');
                        // Sadece remainingAmount güncelle (local state'ten, ekstra Firebase read yok)
                        (async () => {
                          try {
                            const customer = customers.find(c => c.id === customerId);
                            if (customer) {
                              const remaining = await calculateRemainingAmount(customer);
                              setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));
                              await updateDoc(doc(db, 'customers', customerId), {
                                remainingAmount: remaining,
                                updatedAt: new Date()
                              });
                            }
                          } catch (error) {
                            console.warn('Kalan ödeme güncellenirken hata:', error);
                          }
                        })();
                      }}
                      className="cancel-btn"
                      disabled={isLoading}
                    >
                      Kapat
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Sipariş Detayları Bölümü */}
            <div className="order-details-section">
              <OrderDetails
                customerId={editingCustomer.id}
                customerName={editingCustomer.name}
                customerInfo={editingCustomer}
                isLoading={isLoading}
                orderStatus={editingCustomer.orderStatus}
                onOrderStatusChange={(newStatus) => {
                  // OrderDetails zaten Firebase'e yazdı — sadece local state güncelle (çifte write yok)
                  setCustomers(prev => prev.map(c =>
                    c.id === editingCustomer.id ? { ...c, orderStatus: newStatus } : c
                  ));
                  setEditingCustomer(prev => prev ? { ...prev, orderStatus: newStatus } : prev);
                }}
                isEditMode={true}
                permissions={permissions}
                onSaveVadeliSuccess={handleEditCustomerVadeliSaveSuccess}
                onCustomerSaved={handleCustomerPaymentUpdate}
              />
            </div>
          </div>
        </div>
      )}

      <div className="customers-table">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        )}

        {viewMode === 'table' ? (
          // Tablo Görünümü
          <table>
            <thead>
              <tr>
                <th>Ad Soyad</th>
                <th>Telefon 1</th>
                <th>Sipariş Tarihi</th>
                <th>Teslimat Tarihi</th>
                <th>Sipariş Durumu</th>
                <th>Adres</th>
                <th>Kalan Ödeme</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.name}</td>
                  <td>{customer.phone1}</td>
                  <td>{formatDate(customer.orderDate)}</td>
                  <td>{formatDate(customer.deliveryDate)}</td>
                  <td>
                    <span className={`status-badge status-${customer.orderStatus || ORDER_STATUS.NEW_ORDER}`}>
                      {ORDER_STATUS_LABELS[customer.orderStatus || ORDER_STATUS.NEW_ORDER]}
                    </span>
                  </td>
                  <td>{customer.address}</td>
                  <td>
                    {remainingAmounts[customer.id] !== undefined ? (
                      remainingAmounts[customer.id] === -1 ? (
                        <span className="payment-badge payment-no-order-badge">
                          <i className="fas fa-minus-circle"></i>
                          <span>Sipariş Yok</span>
                        </span>
                      ) : (customer.isPaymentCompleted || remainingAmounts[customer.id] === 0) ? (
                        <span className="payment-badge payment-completed-badge">
                          <i className="fas fa-check-circle"></i>
                          <span>Ödeme Alındı</span>
                        </span>
                      ) : (
                        <span className="payment-badge payment-pending-badge">
                          <i className="fas fa-exclamation-circle"></i>
                          <span>{formatPrice(remainingAmounts[customer.id])} ₺</span>
                        </span>
                      )
                    ) : (
                      <span className="payment-badge payment-loading-badge">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Hesaplanıyor...</span>
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {/* Düzenleme/Görüntüleme butonu */}
                      <button
                        onClick={() => handleEditCustomer(customer)}
                        className="edit-btn"
                        disabled={isLoading}
                        title={permissions?.canEditCustomer ? "Düzenle" : "Detaylar"}
                      >
                        <i className={`fas ${permissions?.canEditCustomer ? 'fa-edit' : 'fa-eye'}`}></i>
                      </button>

                      {/* Kopyalama butonu - Montajcı göremez */}
                      {permissions?.canCreateCustomer && (
                        <button
                          onClick={() => handleCopyCustomer(customer)}
                          className="copy-btn"
                          disabled={isLoading}
                          title="Müşteriyi Kopyala"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      )}

                      {/* Silme butonu - Montajcı göremez */}
                      {permissions?.canDeleteCustomer && (
                        <button
                          onClick={() => handleDeleteConfirm(customer)}
                          className="delete-btn"
                          disabled={isLoading}
                          title="Sil"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // Kart Görünümü
          <div className="customers-grid">
            {currentCustomers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onEdit={handleEditCustomer}
                onCopy={handleCopyCustomer}
                onDelete={handleDeleteConfirm}
                isLoading={isLoading}
                formatDate={formatDate}
                remainingAmount={remainingAmounts[customer.id]}
                permissions={permissions}
              />
            ))}
          </div>
        )}

        {currentCustomers.length === 0 && !isLoading && (
          <div className="no-customers">
            <p>Henüz müşteri eklenmemiş.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredCustomers.length > 0 && (
        <div className="pagination-section">
          <div className="pagination-info">
            <span>
              {startIndex + 1}-{Math.min(endIndex, filteredCustomers.length)} / {filteredCustomers.length} müşteri
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              if (page > totalPages) return null;

              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Müşteri Sil</h3>
            <p>
              <strong>{deleteConfirm.name}</strong> adlı müşteriyi silmek istediğinizden emin misiniz?
            </p>
            <p className="warning-text">Bu işlem geri alınamaz!</p>
            <div className="modal-actions">
              <button
                onClick={handleDeleteCustomer}
                className="delete-confirm-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="cancel-btn"
                disabled={isLoading}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList; 